import { PromotionType } from '../../graphQL/commons/types'
import {
  IConditionAction,
  IConfig,
  IPromotion,
  IPromotionShortcut,
  IPromotionUiCatalog,
} from '../../interfaces'
import { GetPromotionsUiDetails, IProduct } from '../../interfaces/catalog'
import { getComboPromotionUiDetails } from './getComboPromotionUiDetails'
import { getPromotionFields } from '../common/getPromotionFields'
import sortByKey from '../sort'
import { getComplexPromotionUiDetails } from './getComplexPromotionUiDetails'

export interface ISetPromotionShortcutsArgs {
  product: IProduct
  config?: IConfig
}

export const UNIQUE_SHORTCUT_IDENTIFIER = 'SHORTCUT_UNIQUE'

const getPromotionDetailsArray: {
  condition: (type: PromotionType) => boolean
  transformAction: GetPromotionsUiDetails
}[] = [
  {
    condition: (type) => type === PromotionType.COMPLEX,
    transformAction: getComplexPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.COMBO,
    transformAction: getComboPromotionUiDetails,
  },
]

const promotionDetailsTransformer: IConditionAction<
  {
    promotionDetails?: IPromotionUiCatalog
    product: IProduct
  },
  IPromotionShortcut[]
>[] = [
  {
    condition: ({ promotionDetails }) =>
      Number(promotionDetails?.data?.customerBuys?.groups?.length) > 1 &&
      !!promotionDetails?.data,
    transformAction: ({ promotionDetails, product }) => {
      const { title = '', description, data } = promotionDetails!
      const { customerBuysGroups } = getPromotionFields(data!)

      const result = customerBuysGroups.map<IPromotionShortcut>((group) => {
        const suffix = group.items.reduce<string>(
          (acc, { sku, quantity }, index) => {
            const isFirst = index === 0
            const prefix = isFirst ? '' : ', '
            if (sku === product.sku) {
              return acc.concat(`${prefix}(${quantity}) ${product.name}`)
            } else {
              return acc.concat(`${prefix}(${quantity}) ${sku}`)
            }
          },
          '',
        )

        return {
          id: group.identifier || '',
          title,
          description: `${description} - ${suffix}.`,
          data: {
            promotion: data,
            groupIdentifier: group.identifier,
          },
          showButton: true,
        }
      })
      return result
    },
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      Number(promotionDetails?.data?.customerGets?.groups?.length) > 1,
    transformAction: ({ promotionDetails }) => [
      {
        id: UNIQUE_SHORTCUT_IDENTIFIER,
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails?.data },
        showButton: false,
      },
    ],
  },
  {
    condition: ({ promotionDetails }) => promotionDetails !== undefined,
    transformAction: ({ promotionDetails }) => [
      {
        id: UNIQUE_SHORTCUT_IDENTIFIER,
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails?.data },
        showButton: true,
      },
    ],
  },
]

export const getPromotionsShortcuts = ({
  product,
  config,
}: ISetPromotionShortcutsArgs): IPromotionShortcut[] => {
  if (product.promotions?.length && config) {
    const sortedPromotions = sortByKey<IPromotion>(
      product.promotions,
      'priority',
    )

    const [promotionToBeDisplayed] = sortedPromotions

    const promotionDetails = getPromotionDetailsArray
      .find(({ condition }) => condition(promotionToBeDisplayed.type))
      ?.transformAction({
        promotion: promotionToBeDisplayed,
        config,
      })

    const validConditionAction = promotionDetailsTransformer.find(
      ({ condition }) => condition({ promotionDetails, product }),
    )

    if (validConditionAction) {
      return [
        ...validConditionAction.transformAction({
          promotionDetails,
          product,
        }),
      ]
    }
  }

  return []
}

export default getPromotionsShortcuts
